import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { useShow, useCustomMutation, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Typography,
  Descriptions,
  Tag,
  Card,
  Space,
  Button,
  Tooltip,
  App,
  Collapse,
} from 'antd';
import {
  LinkOutlined,
  EyeOutlined,
  FormOutlined,
  PictureOutlined,
  FolderOpenOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  EditOutlined,
} from '@ant-design/icons';

import {
  calculatePrice,
  formatPrice,
  type OrderProjection,
  type PageCountBookType,
} from '@woolaby/types';

import { axiosInstance } from '@/providers/axiosInstance';
import type { AdminExample, AdminOrderDetail, OrderStatus } from '@/types';
import { fetchAdminConfig, buildLangSmithUrl } from '@/utils/langsmith';
import { openWithAdminToken } from '@/utils/adminPreview';
import { hasStorageOutput } from '@/utils/storage';
import { RegeneratePageModal } from './RegeneratePageModal';
import { EditRegenerationCountsForm } from './EditRegenerationCountsForm';

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderStatus, string> = {
  INIT: 'default',
  STARTED: 'gold',
  PAYMENT_PENDING: 'gold',
  PAYMENT_COMPLETED: 'blue',
  PAYMENT_FAILED: 'red',
  GENERATING: 'processing',
  GENERATING_PLAN: 'processing',
  GENERATING_CHARACTER_SHEET: 'processing',
  GENERATING_FINAL: 'processing',
  PLAN_REVIEW: 'orange',
  CHARACTER_SHEET_REVIEW: 'orange',
  GENERATION_COMPLETE: 'green',
  GENERATION_FAILED: 'red',
  PREVIEW_READY: 'green',
  CANCELLED: 'default',
  PRINT_JOB_SUBMITTED: 'cyan',
  SHIPPED: 'green',
};

const PREVIEW_ELIGIBLE_STATUSES: OrderStatus[] = [
  'PREVIEW_READY',
  'GENERATION_COMPLETE',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

const CONFIRMATION_ELIGIBLE_STATUSES: OrderStatus[] = [
  'STARTED',
  'PAYMENT_PENDING',
  'PAYMENT_COMPLETED',
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'PREVIEW_READY',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

const GENERATING_ELIGIBLE_STATUSES: OrderStatus[] = [
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'GENERATION_FAILED',
];

const REGENERATE_ELIGIBLE_STATUSES: OrderStatus[] = [
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'GENERATION_FAILED',
];

/** Statuses where an order can be turned into an example */
const EXAMPLE_ELIGIBLE_STATUSES: OrderStatus[] = [
  'PREVIEW_READY',
  'GENERATION_COMPLETE',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

const SUPABASE_STORAGE_URL: string | undefined = import.meta.env
  .VITE_SUPABASE_STORAGE_URL;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return '-';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDurationMs(ms: number | null): string {
  if (ms === null || ms === undefined) return '-';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${String(seconds)}s`;
  return `${String(minutes)}m ${String(seconds)}s`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

/** Render a JSONB object as a pretty-printed code block */
function JsonSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown> | null;
}): ReactElement | null {
  if (!data) return null;

  return (
    <Card size="small" title={title} style={{ marginBottom: 16 }}>
      <pre
        style={{
          maxHeight: 400,
          overflow: 'auto',
          fontSize: 12,
          background: '#f5f5f5',
          padding: 12,
          borderRadius: 6,
          margin: 0,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderShow(): ReactElement {
  const { query } = useShow<AdminOrderDetail>();
  const record = query.data?.data;
  const isLoading = query.isLoading;

  const { message } = App.useApp();
  const { list } = useNavigation();
  const { mutate: makeExample, isLoading: isMakingExample } =
    useCustomMutation<AdminExample>();
  const [madeExample, setMadeExample] = useState(false);
  const [pageRegenModalOpen, setPageRegenModalOpen] = useState(false);
  const [langSmithConfig, setLangSmithConfig] = useState<{
    langSmithOrgId: string | undefined;
    langSmithProjectId: string | undefined;
  } | null>(null);

  // In-flight generation state is read from the LangGraph projection
  // endpoint (`GET /api/admin/orders/:id/projection`).
  // The endpoint returns 404 when the order has no thread yet (e.g. before
  // generation starts) — treat that as "no projection available".
  const [projection, setProjection] = useState<OrderProjection | null>(null);
  const orderId = record?.id;

  useEffect(() => {
    fetchAdminConfig()
      .then(setLangSmithConfig)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!orderId) {
      setProjection(null);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get<OrderProjection>(`/api/admin/orders/${orderId}/projection`)
      .then((response) => {
        if (!cancelled) setProjection(response.data);
      })
      .catch(() => {
        // 404 (no thread / no checkpoint yet) and other errors are non-fatal —
        // the section is hidden when projection is null.
        if (!cancelled) setProjection(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const handleOpenPreview = useCallback(() => {
    if (!record) return;
    openWithAdminToken(`/book/preview/${record.id}`);
  }, [record]);

  const handleOpenConfirmation = useCallback(() => {
    if (!record) return;
    openWithAdminToken(`/book/confirm/${record.id}`);
  }, [record]);

  const handleOpenGenerating = useCallback(() => {
    if (!record) return;
    openWithAdminToken(`/book/generating/${record.id}`);
  }, [record]);

  const handleRegenerate = useCallback(() => {
    if (!record) return;
    axiosInstance
      .post(`/api/admin/orders/${record.id}/regenerate`)
      .then(() => {
        message.success(
          'Order reset to PAYMENT_COMPLETED. You can now open the generating page to restart.'
        );
        query.refetch();
      })
      .catch((error: Error) => {
        message.error(
          `Failed to regenerate: ${error?.message ?? 'Unknown error'}`
        );
      });
  }, [record, message, query]);

  const handleMakeExample = useCallback(() => {
    if (!record) return;
    makeExample(
      {
        url: `/api/admin/orders/${record.id}/make-example`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          setMadeExample(true);
          message.success(
            <span>
              Example created successfully.{' '}
              <a onClick={() => list('examples')}>View examples</a>
            </span>
          );
        },
        onError: (error) => {
          message.error(
            `Failed to create example: ${error?.message ?? 'Unknown error'}`
          );
        },
      }
    );
  }, [record, makeExample, message, list]);

  return (
    <Show isLoading={isLoading}>
      {record && (
        <>
          {/* Quick action buttons */}
          <Space style={{ marginBottom: 24 }}>
            {PREVIEW_ELIGIBLE_STATUSES.includes(record.status) && (
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={handleOpenPreview}
              >
                Open Preview
              </Button>
            )}
            {CONFIRMATION_ELIGIBLE_STATUSES.includes(record.status) && (
              <Button icon={<FormOutlined />} onClick={handleOpenConfirmation}>
                Open Confirmation
              </Button>
            )}
            {GENERATING_ELIGIBLE_STATUSES.includes(record.status) && (
              <Button
                icon={<PlayCircleOutlined />}
                onClick={handleOpenGenerating}
              >
                Open Generating
              </Button>
            )}
            {REGENERATE_ELIGIBLE_STATUSES.includes(record.status) && (
              <Tooltip title="Reset order to PAYMENT_COMPLETED so generation can be restarted">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRegenerate}
                  danger
                >
                  Regenerate
                </Button>
              </Tooltip>
            )}
            {record.status === 'PREVIEW_READY' && (
              <Tooltip title="Regenerate a single page (does not consume the user's page regeneration count)">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setPageRegenModalOpen(true)}
                >
                  Regenerate Page
                </Button>
              </Tooltip>
            )}
            {record.workflowRunId &&
              buildLangSmithUrl(record.workflowRunId, langSmithConfig) && (
                <Tooltip title="Open in LangSmith">
                  <Button
                    icon={<LinkOutlined />}
                    href={
                      buildLangSmithUrl(record.workflowRunId, langSmithConfig)!
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LangSmith
                  </Button>
                </Tooltip>
              )}
            {hasStorageOutput(record) && SUPABASE_STORAGE_URL && (
              <Tooltip title="Open Supabase Storage folder">
                <Button
                  icon={<FolderOpenOutlined />}
                  href={`${SUPABASE_STORAGE_URL}?prefix=${record.id}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Storage
                </Button>
              </Tooltip>
            )}
            {EXAMPLE_ELIGIBLE_STATUSES.includes(record.status) && (
              <Tooltip title="Create a showcase example from this order">
                <Button
                  icon={<PictureOutlined />}
                  onClick={handleMakeExample}
                  loading={isMakingExample}
                  disabled={madeExample}
                >
                  {madeExample ? 'Example Created' : 'Make Example'}
                </Button>
              </Tooltip>
            )}
          </Space>

          {/* Core order information */}
          <Title level={5}>Order Information</Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Order ID">
              <Text copyable>{record.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLORS[record.status] ?? 'default'}>
                {record.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Book Title">
              {record.bookTitle ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Plot Summary">
              <div style={{ maxWidth: '25vw' }}>
                <Text ellipsis={{ tooltip: record.plotSummary ?? '-' }}>
                  {record.plotSummary ?? '-'}
                </Text>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {record.email ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {record.phone ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Client">
              {record.client}
            </Descriptions.Item>
            <Descriptions.Item label="App Environment">
              {record.appEnv ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Page Count">
              {record.pageCount ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Order Type">
              {record.paidProductType ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {formatDate(record.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {formatDate(record.updatedAt)}
            </Descriptions.Item>
          </Descriptions>

          {/* Payment information */}
          <Title level={5} style={{ marginTop: 24 }}>
            Payment
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Payment Status">
              {record.paymentStatus ? (
                <Tag
                  color={record.paymentStatus === 'paid' ? 'green' : 'orange'}
                >
                  {record.paymentStatus}
                </Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Amount Total">
              {formatCents(record.amountTotal)}
            </Descriptions.Item>
            <Descriptions.Item label="Configured Price">
              {record.paidProductType && record.pageCount !== null
                ? formatPrice(
                    calculatePrice(
                      record.paidProductType as PageCountBookType,
                      record.pageCount
                    )
                  )
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Stripe Session ID">
              {record.stripeSessionId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.stripeSessionId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Stripe Payment Intent">
              {record.stripePaymentIntentId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.stripePaymentIntentId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* Cost breakdown */}
          <Title level={5} style={{ marginTop: 24 }}>
            Costs
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="AI Cost">
              {formatCents(record.aiCostCents)}
            </Descriptions.Item>
            <Descriptions.Item label="AI Duration">
              {formatDurationMs(record.generationDurationMs)}
            </Descriptions.Item>
            <Descriptions.Item label="Lulu Print Cost">
              {formatCents(record.luluPrintCostCents)}
            </Descriptions.Item>
            <Descriptions.Item label="Lulu Ship Cost">
              {formatCents(record.luluShipCostCents)}
            </Descriptions.Item>
          </Descriptions>

          {/* Generation details */}
          <Title level={5} style={{ marginTop: 24 }}>
            Generation
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Thread ID">
              {record.threadId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.threadId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Workflow Run ID">
              {record.workflowRunId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.workflowRunId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Generated Path">
              {record.generatedPath ? (
                SUPABASE_STORAGE_URL ? (
                  <a
                    href={`${SUPABASE_STORAGE_URL}?prefix=${record.id}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {record.generatedPath}
                  </a>
                ) : (
                  record.generatedPath
                )
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Session ID">
              {record.sessionId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.sessionId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Page Text Font Size">
              {record.pageTextFontSize !== null
                ? `${record.pageTextFontSize}px`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Retry Attempts">
              {/*
                Phase 5 — derived from LangGraph `humanPlanApprovalCount`.
                Counts user-approved plans that were rejected / resumed.
              */}
              {record.retryAttempts}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Reason" span={2}>
              {record.failureReason ? (
                <Text type="danger">{record.failureReason}</Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Message" span={2}>
              {/*
                Raw dev-jargon failureMessage is shown verbatim — the leak is
                intentional (helps customer support triage). See memory note
                `feedback_failure_message_leak_intentional.md`.
              */}
              {record.failureMessage ? (
                <Text
                  type="danger"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {record.failureMessage}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Category" span={2}>
              {/*
                Issue #677 — recovery class from the AgentError taxonomy
                (e.g. PERMANENT, TRANSIENT, SAFETY). Null for legacy / infra
                failures that predate the taxonomy.
              */}
              {record.failureCategory ? (
                <Tag color="red">{record.failureCategory}</Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          {/*
            Issue #677 — structured failure diagnostics (per-page retry
            audit, global regen counter, last skill name). Rendered as a
            collapsible pretty-printed JSON block. Only shown when there
            is actual diagnostic payload to display so healthy orders
            don't carry empty noise.
          */}
          {record.failureDetails && (
            <Collapse
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'failure-details',
                  label: 'Failure Details (issue #677 diagnostics)',
                  children: (
                    <pre
                      style={{
                        maxHeight: 400,
                        overflow: 'auto',
                        fontSize: 12,
                        background: '#f5f5f5',
                        padding: 12,
                        borderRadius: 6,
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(record.failureDetails, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          )}

          {/* Lulu / shipping */}
          <Title level={5} style={{ marginTop: 24 }}>
            Print / Shipping
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Lulu Job ID">
              {record.luluJobId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Lulu Status">
              {record.luluStatus ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tracking URL" span={2}>
              {record.luluTrackingUrl ? (
                <a
                  href={record.luluTrackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {record.luluTrackingUrl}
                </a>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* JSONB data sections */}
          <Title level={5} style={{ marginTop: 24 }}>
            Data
          </Title>
          <JsonSection title="Survey Data" data={record.surveyJson} />
          {/*
            In-flight generation state (latest plan, latest character sheet,
            human review history, last error) is sourced from the LangGraph
            projection endpoint.
          */}
          {projection && (
            <JsonSection
              title="Latest Plan (LangGraph projection)"
              data={
                projection.latestPlan as unknown as Record<
                  string,
                  unknown
                > | null
              }
            />
          )}
          {projection?.latestCharacterSheet && (
            <JsonSection
              title="Latest Character Sheet (LangGraph projection)"
              data={
                projection.latestCharacterSheet as unknown as Record<
                  string,
                  unknown
                >
              }
            />
          )}
          {projection && projection.humanReviewHistory.length > 0 && (
            <JsonSection
              title="Human Review History (LangGraph projection)"
              data={
                {
                  events: projection.humanReviewHistory,
                } as unknown as Record<string, unknown>
              }
            />
          )}
          {projection?.lastError && (
            <JsonSection
              title="Last Error (LangGraph projection)"
              data={projection.lastError as unknown as Record<string, unknown>}
            />
          )}
          <JsonSection title="Billing Address" data={record.billingAddress} />
          <JsonSection title="Shipping Address" data={record.shippingAddress} />
          {record.environmentConfigJson && (
            <JsonSection
              title="Environment Config"
              data={record.environmentConfigJson}
            />
          )}

          {/* Bulk-edit remaining regeneration attempts */}
          <EditRegenerationCountsForm
            orderId={record.id}
            coverRegenerationCount={record.coverRegenerationCount}
            bookRegenerationCount={record.bookRegenerationCount}
            pageRegenerationCount={record.pageRegenerationCount}
            onSuccess={() => {
              query.refetch();
            }}
          />

          <RegeneratePageModal
            open={pageRegenModalOpen}
            onClose={() => setPageRegenModalOpen(false)}
            orderId={record.id}
            onSuccess={() => {
              query.refetch();
            }}
          />
        </>
      )}
    </Show>
  );
}
